// File: RegisterPage.jsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Eye,
  EyeOff,
  Landmark,
  Mail,
  MapPin,
  Phone,
  Search,
  UserRound,
} from 'lucide-react';
import './RegisterPage.css';
import { apiEndpoint } from '../config/api';

const REGISTER_API_URL =
  process.env.REACT_APP_REGISTER_API_URL ||
  apiEndpoint('api/register.php');

const emptyForm = {
  company: '',
  ownerName: '',
  birthDate: '',
  gender: 'Nam',
  identityNumber: '',
  taxCode: '',
  email: '',
  password: '',
  confirmPassword: '',
  bankName: '',
  accountNumber: '',
  accountHolder: '',
  capital: '',
  phone: '',
  city: '',
  district: '',
  address: '',
  contactPerson: '',
  acceptTerms: false,
};

export default function RegisterPage() {
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const maximumDate = useMemo(
    () => new Date().toISOString().slice(0, 10),
    []
  );

  const update = ({ target }) => {
    const value =
      target.type === 'checkbox'
        ? target.checked
        : target.value;

    setForm((current) => ({
      ...current,
      [target.name]: value,
    }));

    setMessage(null);
  };

  const updateNumberOnly = ({ target }) => {
    const value = target.value.replace(/\D/g, '');

    setForm((current) => ({
      ...current,
      [target.name]: value,
    }));

    setMessage(null);
  };

  const updateTaxCode = ({ target }) => {
    const value = target.value
      .replace(/[^\d-]/g, '')
      .slice(0, 14);

    setForm((current) => ({
      ...current,
      taxCode: value,
    }));

    setMessage(null);
  };

  const updateCapital = ({ target }) => {
    const numberOnly = target.value.replace(/\D/g, '');
    const formatted = numberOnly.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      '.'
    );

    setForm((current) => ({
      ...current,
      capital: formatted,
    }));

    setMessage(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    setMessage(null);

    const company = form.company.trim();
    const ownerName = form.ownerName.trim();
    const email = form.email.trim().toLowerCase();
    const bankName = form.bankName.trim();
    const accountHolder = form.accountHolder.trim();
    const identityNumber = form.identityNumber.replace(/\D/g, '');
    const accountNumber = form.accountNumber.replace(
      /[^0-9A-Za-z]/g,
      ''
    );
    const phone = form.phone.replace(/\D/g, '');

    if (company.length < 2) {
      setMessage({
        type: 'error',
        text: 'Vui lòng nhập tên doanh nghiệp.',
      });
      return;
    }

    if (ownerName.length < 2) {
      setMessage({
        type: 'error',
        text: 'Vui lòng nhập họ tên người đại diện.',
      });
      return;
    }

 

    if (!/^\d{10}(-\d{3})?$/.test(form.taxCode)) {
      setMessage({
        type: 'error',
        text: 'Mã số thuế phải gồm 10 chữ số hoặc dạng 10 chữ số-3 chữ số.',
      });
      return;
    }

    if (!email.includes('@')) {
      setMessage({
        type: 'error',
        text: 'Địa chỉ email không hợp lệ.',
      });
      return;
    }

    if (form.password.length < 8) {
      setMessage({
        type: 'error',
        text: 'Mật khẩu phải có ít nhất 8 ký tự.',
      });
      return;
    }

    if (form.password !== form.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Mật khẩu xác nhận không khớp.',
      });
      return;
    }

    if (!bankName) {
      setMessage({
        type: 'error',
        text: 'Vui lòng nhập tên ngân hàng.',
      });
      return;
    }

    if (accountHolder.length < 2) {
      setMessage({
        type: 'error',
        text: 'Vui lòng nhập tên chủ tài khoản.',
      });
      return;
    }

    if (phone.length < 9 || phone.length > 11) {
      setMessage({
        type: 'error',
        text: 'Số điện thoại không hợp lệ.',
      });
      return;
    }

    if (!form.acceptTerms) {
      setMessage({
        type: 'error',
        text: 'Vui lòng đồng ý với điều khoản sử dụng.',
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(REGISTER_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        cache: 'no-store',
        body: JSON.stringify({
          company,
          ownerName,
          birthDate: form.birthDate,
          gender: form.gender,

          // Gửi đầy đủ thay vì chỉ 4 số cuối.
          identityNumber,
          taxCode: form.taxCode,

          email,
          password: form.password,

          bankName,
          // Gửi đầy đủ thay vì chỉ 4 số cuối.
          accountNumber,
          accountHolder,

          capital: form.capital.replace(/\D/g, ''),
          phone,
          city: form.city.trim(),
          district: form.district.trim(),
          address: form.address.trim(),
          contactPerson: form.contactPerson.trim(),
        }),
      });

      const rawText = await response.text();

      let result;

      try {
        result = rawText ? JSON.parse(rawText) : null;
      } catch {
        throw new Error('Máy chủ không trả về JSON hợp lệ.');
      }

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message || 'Không thể đăng ký tài khoản.'
        );
      }

      setMessage({
        type: 'success',
        text:
          result.message ||
          'Đăng ký tài khoản thành công.',
      });

      setForm(emptyForm);
    } catch (error) {
      setMessage({
        type: 'error',
        text:
          error.message ||
          'Không thể kết nối máy chủ. Vui lòng thử lại.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="register-page">
      <section className="register-container">
        <header className="register-header">
          <div className="register-logo">
            <img
              src="https://dangkythuedientu.com/img/loading.42dd77aa.gif"
              alt="Logo hệ thống"
              className="w-48 h-48 object-contain"
              referrerPolicy="no-referrer"
            />
          </div>

          <p className="register-system-name">
            HỆ THỐNG THUẾ ĐIỆN TỬ
          </p>

          <h1>Đăng ký tài khoản doanh nghiệp</h1>

          <p className="register-subtitle">
            Quý khách vui lòng nhập thông tin cơ bản
          </p>
        </header>

        <section className="register-card">
          <form
            className="register-form"
            onSubmit={submit}
            autoComplete="off"
          >
            <InputField
              icon={<Building2 size={19} />}
              name="company"
              value={form.company}
              onChange={update}
              placeholder="Tên công ty"
              maxLength={150}
              required
            />

            <InputField
              icon={<UserRound size={19} />}
              name="ownerName"
              value={form.ownerName}
              onChange={update}
              placeholder="Họ và tên chủ doanh nghiệp"
              maxLength={100}
              required
            />

            <InputField
              icon={<CalendarDays size={19} />}
              type="date"
              name="birthDate"
              value={form.birthDate}
              onChange={update}
              max={maximumDate}
              required
            />

            <fieldset className="register-gender">
              <legend>Giới tính</legend>

              <div className="register-radio-list">
                {['Nam', 'Nữ', 'Khác'].map((gender) => (
                  <label key={gender}>
                    <input
                      type="radio"
                      name="gender"
                      value={gender}
                      checked={form.gender === gender}
                      onChange={update}
                    />
                    <span>{gender}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <InputField
              icon={<CreditCard size={19} />}
              name="identityNumber"
              value={form.identityNumber}
              onChange={updateNumberOnly}
              placeholder="Nhập đầy đủ số CMND / CCCD"
              inputMode="numeric"
              minLength={9}
              maxLength={12}
              required
            />

            <InputField
              icon={<Building2 size={19} />}
              name="taxCode"
              value={form.taxCode}
              onChange={updateTaxCode}
              placeholder="Mã số thuế"
              inputMode="numeric"
              maxLength={14}
              required
            />

            <InputField
              icon={<Mail size={19} />}
              type="email"
              name="email"
              value={form.email}
              onChange={update}
              placeholder="Email doanh nghiệp"
              maxLength={190}
              required
            />

            <PasswordInput
              name="password"
              value={form.password}
              onChange={update}
              placeholder="Mật khẩu"
              visible={showPassword}
              onToggle={() =>
                setShowPassword((current) => !current)
              }
            />

            <PasswordInput
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={update}
              placeholder="Xác nhận mật khẩu"
              visible={showConfirmPassword}
              onToggle={() =>
                setShowConfirmPassword((current) => !current)
              }
            />

            <InputField
              icon={<Search size={19} />}
              name="bankName"
              value={form.bankName}
              onChange={update}
              placeholder="Nhập tên ngân hàng"
              maxLength={100}
              required
            />

            <InputField
              icon={<CreditCard size={19} />}
              name="accountNumber"
              value={form.accountNumber}
              onChange={updateNumberOnly}
              placeholder="Nhập đầy đủ số tài khoản"
              inputMode="numeric"
              minLength={6}
              maxLength={30}
              required
            />

            <InputField
              icon={<UserRound size={19} />}
              name="accountHolder"
              value={form.accountHolder}
              onChange={update}
              placeholder="Tên chủ tài khoản"
              maxLength={100}
              required
            />

            <InputField
              icon={<Landmark size={19} />}
              name="capital"
              value={form.capital}
              onChange={updateCapital}
              placeholder="Vốn điều lệ, ví dụ 1.000.000.000"
              inputMode="numeric"
              maxLength={25}
              required
            />

            <InputField
              icon={<Phone size={19} />}
              type="tel"
              name="phone"
              value={form.phone}
              onChange={update}
              placeholder="Nhập số điện thoại"
              inputMode="tel"
              maxLength={15}
              required
            />

            <div className="register-section-title">
              <MapPin size={19} />
              <h2>Địa chỉ trụ sở đăng ký</h2>
            </div>

            <InputField
              name="city"
              value={form.city}
              onChange={update}
              placeholder="Thành phố / tỉnh thành"
              maxLength={100}
              required
            />

            <InputField
              name="district"
              value={form.district}
              onChange={update}
              placeholder="Phường / xã"
              maxLength={100}
              required
            />

            <InputField
              name="address"
              value={form.address}
              onChange={update}
              placeholder="Địa chỉ cụ thể"
              maxLength={255}
              required
            />

            <InputField
              icon={<UserRound size={19} />}
              name="contactPerson"
              value={form.contactPerson}
              onChange={update}
              placeholder="Cán bộ phụ trách"
              maxLength={100}
              required
            />

            <label className="register-terms">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={form.acceptTerms}
                onChange={update}
              />

              <span>
                Tôi đã đọc, hiểu rõ và chấp nhận các{' '}
                <Link to="/terms">điều khoản</Link> sử dụng của hệ
                thống.
              </span>
            </label>

            {message && (
              <div
                className={`register-message ${message.type}`}
                role="alert"
                aria-live="polite"
              >
                {message.type === 'success' && (
                  <CheckCircle2 size={19} />
                )}
                <span>{message.text}</span>
              </div>
            )}

            <button
              className="register-submit"
              type="submit"
              disabled={submitting}
            >
              {submitting
                ? 'Đang kiểm tra thông tin...'
                : 'Đăng ký tài khoản'}
            </button>

            <p className="register-login">
              Bạn đã có tài khoản?{' '}
              <Link to="/login">Đăng nhập</Link>
            </p>
          </form>
        </section>
      </section>
    </main>
  );
}

function InputField({
  icon,
  type = 'text',
  className = '',
  ...props
}) {
  return (
    <div className={`register-input-wrap ${className}`}>
      {icon && (
        <span className="register-input-icon">{icon}</span>
      )}

      <input
        type={type}
        className={icon ? 'has-icon' : ''}
        autoComplete="off"
        {...props}
      />
    </div>
  );
}

function PasswordInput({
  name,
  value,
  visible,
  onToggle,
  onChange,
  placeholder,
}) {
  return (
    <div className="register-input-wrap register-password-wrap">
      <span className="register-input-icon">
        <Eye size={19} />
      </span>

      <input
        type={visible ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="new-password"
        minLength={8}
        maxLength={128}
        required
        className="has-icon"
      />

      <button
        type="button"
        className="register-password-toggle"
        onClick={onToggle}
        aria-label={
          visible ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'
        }
      >
        {visible ? <EyeOff size={19} /> : <Eye size={19} />}
      </button>
    </div>
  );
}
